import React from "react";
import { Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SettingsNav, { DEFAULT_SECTION, findSection } from "../components/settings/SettingsNav";
import BrandingSettings from "../components/settings/BrandingSettings";
import AiSettings from "../components/settings/AiSettings";
import PermissionSettings from "../components/settings/PermissionSettings";
import ThresholdSettings from "../components/settings/ThresholdSettings";
import AutomationSettings from "../components/settings/AutomationSettings";
import ApiKeySettings from "../components/settings/ApiKeySettings";
import IntegrationSettings from "../components/settings/IntegrationSettings";

/**
 * Settings, as a shell around one section at a time.
 *
 * This page used to be 677 lines and six cards stacked in a 672-pixel column,
 * which had two concrete costs: you scrolled away from the brand preview while
 * editing the fields it previews, and every visit fetched five endpoints
 * regardless of what you came to change. Each section now owns its own data
 * loading and its own errors.
 *
 * Sub-routes rather than tab state: README and DEPLOYMENT both direct an
 * operator to "Settings > Branding", which is now a link, and the back button
 * behaves. `SettingsNav`'s `SECTIONS` is the single source of truth for which
 * segments are valid.
 */

const SECTION_COMPONENTS = {
  branding: BrandingSettings,
  ai: AiSettings,
  permissions: PermissionSettings,
  thresholds: ThresholdSettings,
  automation: AutomationSettings,
  "api-keys": ApiKeySettings,
  integrations: IntegrationSettings,
};

export default function SettingsPage() {
  const { t } = useTranslation();
  const { section: requested } = useParams();

  // A bare /settings, or a segment that isn't a section, resolves to the first
  // one -- replace: true so it doesn't leave a dead entry in the back stack.
  const section = findSection(requested);
  if (!section) {
    return <Navigate to={`/settings/${DEFAULT_SECTION}`} replace />;
  }

  const Section = SECTION_COMPONENTS[section.key];

  // Title, then the section bar, then the section -- the bar reads as belonging
  // to the title, so it sits tight under it and the air goes below the hairline.
  return (
    <div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-4">
        {t("settings.title")}
      </h1>

      <SettingsNav />

      {/* `key` remounts on section change, so a section can't inherit stale
          form state or a stale error from the one before it. */}
      <div className="mt-6">
        <Section key={section.key} />
      </div>
    </div>
  );
}
